import axios from 'axios';
import {
  ChatUnreadSource,
  ChatUnreadTelemetrySink,
  emitChatUnreadTelemetry,
  getChatUnreadCountBucket
} from './chatUnreadTelemetry';
import type {
  ZendeskConversationField,
  ZendeskOutgoingMessage,
  ZendeskSupportContextAdapter,
  ZendeskSupportScalar
} from './zendeskSupportContextTypes';

export type ZendeskMessagingStatus =
  | 'disabled'
  | 'loading'
  | 'ready'
  | 'authenticating'
  | 'authenticated'
  | 'identity-conflict'
  | 'anonymous'
  | 'unavailable';

export type ZendeskMessagingMode = 'floating' | 'embedded';

export type ZendeskUnavailableReason =
  | 'missing-snippet-key'
  | 'invalid-snippet-key'
  | 'snippet-load-failed'
  | 'readiness-timeout'
  | 'sdk-unavailable'
  | 'mode-conflict'
  | 'authentication-failed'
  | 'identity-conflict'
  | 'service-unavailable'
  | 'render-failed'
  | 'unknown';

export interface ZendeskMessagingState {
  status: ZendeskMessagingStatus;
  mode: ZendeskMessagingMode | null;
  unavailableReason?: ZendeskUnavailableReason;
}

export type ZendeskUnreadState =
  | { status: 'unknown'; count: null }
  | { status: 'known'; count: number }
  | { status: 'unavailable'; count: null };

export interface ZendeskMessagingSession {
  userId: string | number;
  accessToken: string;
}

export interface ZendeskEmbeddedTargets {
  widget?: string;
  conversationList?: string;
  messageLog?: string;
}

export interface ZendeskEmbeddedTargetElement {
  targetElement: string;
}

export interface ZendeskEmbeddedWireTargets {
  widget?: ZendeskEmbeddedTargetElement;
  conversationList?: ZendeskEmbeddedTargetElement;
  messageLog?: ZendeskEmbeddedTargetElement;
}

export interface ZendeskMessagingTheme {
  primary: string;
  onPrimary: string;
  message: string;
  onMessage: string;
  action: string;
  onAction: string;
  businessMessage: string;
  onBusinessMessage: string;
  background: string;
  onBackground: string;
  conversationListBackground: string;
  onConversationListBackground: string;
  error: string;
  onError: string;
  notify: string;
  onNotify: string;
  onSecondaryAction: string;
}

export interface ZendeskMessagingCustomization {
  theme: ZendeskMessagingTheme;
  common?: {
    hideHeader: boolean;
  };
  conversationList?: {
    hideHeader: boolean;
  };
  messageLog?: {
    hideHeader: boolean;
  };
}

export interface ZendeskMessagingCloseEvent {
  id: string;
  createdAt: number;
  type: 'messagingClosed';
}

export interface ZendeskConversationOpenedEvent {
  id: string;
  createdAt: number;
  type: 'conversationOpened';
  payload: {
    conversation: {
      id: string | null;
      status: 'active' | 'idle' | null;
    };
  };
}

type ZendeskEmbeddedTargetName = keyof ZendeskEmbeddedTargets;

interface ResolvedZendeskEmbeddedTarget {
  name: ZendeskEmbeddedTargetName;
  selector: string;
  element: Element;
}

type ZendeskEmbeddedBinding = ReadonlyArray<ResolvedZendeskEmbeddedTarget>;

interface ResolvedZendeskEmbeddedRender {
  binding: ZendeskEmbeddedBinding;
  wireTargets: ZendeskEmbeddedWireTargets;
}

interface ZendeskEmbeddedRenderInFlight {
  generation: number;
  binding: ZendeskEmbeddedBinding;
  promise: Promise<void>;
}

export type ZendeskRenderConfig =
  | { mode: 'floating' }
  | ({ mode: 'embedded' } & ZendeskEmbeddedWireTargets);

export interface ZendeskSdkError {
  message?: string;
  reason?: string;
  type?: string;
}

type ZendeskErrorCallback = (error?: ZendeskSdkError | null) => void;
type ZendeskJwtCallback = (jwt: string) => void;
export type ZendeskJwtProvider = (callback: ZendeskJwtCallback) => Promise<void>;

export interface ZendeskMessengerApi {
  (namespace: 'messenger', command: 'open'): void;
  (namespace: 'messenger', command: 'close' | 'hide' | 'show'): void;
  (
    namespace: 'messenger',
    command: 'render',
    config: ZendeskRenderConfig,
    callback?: ZendeskErrorCallback
  ): void;
  (
    namespace: 'messenger',
    command: 'loginUser',
    jwtProvider: (callback: ZendeskJwtCallback) => void,
    callback?: ZendeskErrorCallback
  ): void;
  (namespace: 'messenger', command: 'logoutUser'): void;
  (namespace: 'messenger:set', command: 'locale', locale: string): void;
  (
    namespace: 'messenger:set',
    command: 'customization',
    customization: ZendeskMessagingCustomization
  ): void;
  (
    namespace: 'messenger:set',
    command: 'conversationFields',
    fields: ZendeskConversationField[],
    callback?: (error?: unknown) => void
  ): void;
  (
    namespace: 'messenger:set',
    command: 'conversationTags',
    tags: string[]
  ): void;
  (
    namespace: 'messenger:set',
    command: 'conversationMetadata',
    metadata: Record<string, ZendeskSupportScalar>,
    callback?: (error?: unknown) => void
  ): void;
  (
    namespace: 'messenger:set',
    command: 'beforeMessageSent',
    callback: (message: ZendeskOutgoingMessage, data: { conversationId?: string }) => ZendeskOutgoingMessage
  ): (() => void) | void;
  (
    namespace: 'messenger:on',
    command: 'close',
    callback: (event: ZendeskMessagingCloseEvent) => void
  ): () => void;
  (
    namespace: 'messenger:on',
    command: 'conversationOpened',
    callback: (event: ZendeskConversationOpenedEvent) => void
  ): () => void;
  (
    namespace: 'messenger:on',
    command: 'unreadMessages',
    callback: (count: number) => void
  ): (() => void) | void;
}

declare global {
  interface Window {
    zE?: ZendeskMessengerApi;
    zEMessenger?: {
      autorender?: boolean;
    };
  }
}

export interface ZendeskMessagingJwtResponse {
  jwt: string;
  expiresIn: number;
  requiresIdentityFinalization?: boolean;
}

export type ZendeskMessagingJwtFetcher = (
  accessToken: string
) => Promise<ZendeskMessagingJwtResponse>;

export type ZendeskMessagingIdentityAction = (
  accessToken: string
) => Promise<ZendeskMessagingJwtResponse>;

export interface ZendeskMessagingSdkAdapter extends ZendeskSupportContextAdapter {
  ensureReady(snippetKey: string, timeoutMs: number): Promise<void>;
  setLocale(locale: string): void;
  setCustomization(customization: ZendeskMessagingCustomization): void;
  subscribeToClose(listener: () => void): () => void;
  subscribeToConversationOpened?(listener: () => void): () => void;
  subscribeToUnreadMessages(listener: (count: number) => void): (() => void) | undefined;
  deactivate(): void;
  open(): void;
  renderFloating(): Promise<void>;
  loginUser(jwtProvider: ZendeskJwtProvider, timeoutMs: number, isCurrent?: (initial: boolean) => boolean): Promise<void>;
  renderEmbedded(targets: ZendeskEmbeddedTargets): Promise<void>;
  logoutUser(timeoutMs: number): Promise<void>;
  resetSession(): void;
}

export const isZendeskMessagingEnabled =
  process.env.REACT_APP_ZENDESK_MESSAGING_ENABLED === 'true';

const DEFAULT_READY_TIMEOUT_MS = 10000;
const DEFAULT_AUTH_TIMEOUT_MS = 10000;
const JWT_REQUEST_TIMEOUT_MS = 30000;
const DEFAULT_LOGOUT_TIMEOUT_MS = 2000;
const SNIPPET_ID = 'ze-snippet';
const SNIPPET_BASE_URL = 'https://static.zdassets.com/ekr/snippet.js';
const SNIPPET_KEY_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;
const EMBEDDED_TARGET_NAMES: ReadonlyArray<ZendeskEmbeddedTargetName> = [
  'conversationList',
  'messageLog',
  'widget'
];

const transitionTable: Record<ZendeskMessagingStatus, ZendeskMessagingStatus[]> = {
  disabled: ['disabled', 'loading', 'unavailable'],
  loading: ['loading', 'ready', 'unavailable', 'disabled'],
  ready: ['ready', 'authenticating', 'unavailable', 'disabled'],
  authenticating: ['authenticating', 'authenticated', 'anonymous', 'identity-conflict', 'ready', 'unavailable', 'disabled'],
  authenticated: ['authenticated', 'identity-conflict', 'loading', 'ready', 'unavailable', 'disabled'],
  'identity-conflict': ['identity-conflict', 'ready', 'authenticating', 'anonymous', 'loading', 'unavailable', 'disabled'],
  anonymous: ['anonymous', 'loading', 'ready', 'unavailable', 'disabled'],
  unavailable: ['unavailable', 'loading', 'ready', 'authenticated', 'identity-conflict', 'anonymous', 'disabled']
};

export const isZendeskMessagingTransitionValid = (
  current: ZendeskMessagingStatus,
  next: ZendeskMessagingStatus
) => transitionTable[current].includes(next);

export const mapIokLanguageToZendeskLocale = (language?: string | null): string => {
  const normalized = language?.trim().toUpperCase();

  if (normalized === 'EN' || normalized?.startsWith('EN-')) return 'en-US';
  if (normalized === 'ES' || normalized?.startsWith('ES-')) return 'es-ES';
  if (normalized === 'PT' || normalized?.startsWith('PT-')) return 'pt-BR';

  return 'pt-BR';
};

const getHttpStatus = (error: unknown): number | undefined => {
  const response = (error as { response?: { status?: unknown } } | null)?.response;
  return typeof response?.status === 'number' ? response.status : undefined;
};

const isIdentityConflictError = (error: unknown): boolean => {
  if (error instanceof ZendeskMessagingError) return error.code === 'identity-conflict';
  if (getHttpStatus(error) === 409) return true;
  const sdkError = error as (ZendeskSdkError & { status?: unknown; statusCode?: unknown }) | null;
  const details = [sdkError?.message, sdkError?.reason, sdkError?.type, sdkError?.status, sdkError?.statusCode]
    .filter((value) => value !== undefined && value !== null)
    .join(' ')
    .toLowerCase();
  return details.includes('409') || details.includes('conflict');
};

export const fetchZendeskMessagingJwt: ZendeskMessagingJwtFetcher = async (
  accessToken
) => {
  const response = await axios.request<ZendeskMessagingJwtResponse>({
    baseURL: process.env.REACT_APP_API_URL,
    url: 'zendesk/messaging/identity/prepare',
    method: 'post',
    timeout: JWT_REQUEST_TIMEOUT_MS,
    responseType: 'json',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      'x-access-token': accessToken
    }
  });

  const { jwt, expiresIn, requiresIdentityFinalization } = response.data;
  if (typeof jwt !== 'string' || jwt.length === 0 || typeof expiresIn !== 'number') {
    throw new ZendeskMessagingError(
      'authentication-failed',
      'Zendesk Messaging returned an invalid authentication response'
    );
  }

  return {
    jwt,
    expiresIn,
    ...(requiresIdentityFinalization === true ? { requiresIdentityFinalization: true } : {})
  };
};

const postZendeskMessagingIdentityAction = async (
  accessToken: string,
  url: string
): Promise<ZendeskMessagingJwtResponse> => {
  const response = await axios.request<ZendeskMessagingJwtResponse>({
    baseURL: process.env.REACT_APP_API_URL,
    url,
    method: 'post',
    timeout: JWT_REQUEST_TIMEOUT_MS,
    responseType: 'json',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      'x-access-token': accessToken
    }
  });
  const { jwt, expiresIn, requiresIdentityFinalization } = response.data;
  if (typeof jwt !== 'string' || !jwt || typeof expiresIn !== 'number') {
    throw new ZendeskMessagingError('authentication-failed', 'Zendesk Messaging returned an invalid identity response');
  }
  return {
    jwt,
    expiresIn,
    ...(requiresIdentityFinalization === true ? { requiresIdentityFinalization: true } : {})
  };
};

export const useExistingZendeskMessagingIdentity: ZendeskMessagingIdentityAction = (accessToken) =>
  postZendeskMessagingIdentityAction(accessToken, 'zendesk/messaging/identity/use-existing');

export const finalizeZendeskMessagingIdentity: ZendeskMessagingIdentityAction = (accessToken) =>
  postZendeskMessagingIdentityAction(accessToken, 'zendesk/messaging/identity/finalize');

export class ZendeskMessagingError extends Error {
  constructor(
    public readonly code: ZendeskUnavailableReason,
    message: string
  ) {
    super(message);
    this.name = 'ZendeskMessagingError';
  }
}

const toUnavailableReason = (error: unknown): ZendeskUnavailableReason => {
  if (error instanceof ZendeskMessagingError) return error.code;
  return 'unknown';
};

const snippetUrl = (snippetKey: string): string =>
  `${SNIPPET_BASE_URL}?key=${encodeURIComponent(snippetKey)}`;

const scriptHasSnippetKey = (
  script: HTMLScriptElement,
  snippetKey: string
): boolean => {
  try {
    return new URL(script.src, window.location.href).searchParams.get('key') === snippetKey;
  } catch {
    return false;
  }
};

const embeddedBindingsMatch = (
  left: ZendeskEmbeddedBinding,
  right: ZendeskEmbeddedBinding
): boolean =>
  left.length === right.length &&
  left.every(
    (target, index) =>
      target.name === right[index].name &&
      target.selector === right[index].selector &&
      target.element === right[index].element
  );

const hasConnectedEmbeddedTarget = (binding: ZendeskEmbeddedBinding): boolean =>
  binding.some(({ element }) => element.isConnected);

const hasEveryEmbeddedTargetConnected = (
  binding: ZendeskEmbeddedBinding
): boolean => binding.every(({ element }) => element.isConnected);

export class BrowserZendeskMessagingAdapter implements ZendeskMessagingSdkAdapter {
  private readyPromise?: Promise<void>;
  private floatingRenderPromise?: Promise<void>;
  private renderedMode: ZendeskMessagingMode | null = null;
  private embeddedBinding?: ZendeskEmbeddedBinding;
  private embeddedRenderInFlight?: ZendeskEmbeddedRenderInFlight;
  private embeddedRenderGeneration = 0;
  private appliedCustomizationKey?: string;

  private getApi(): ZendeskMessengerApi {
    if (typeof window.zE !== 'function') {
      throw new ZendeskMessagingError(
        'sdk-unavailable',
        'Zendesk Messaging SDK is unavailable'
      );
    }
    return window.zE;
  }

  ensureReady(snippetKey: string, timeoutMs: number): Promise<void> {
    if (this.readyPromise) return this.readyPromise;

    this.readyPromise = new Promise<void>((resolve, reject) => {
      window.zEMessenger = {
        ...window.zEMessenger,
        autorender: false
      };

      const existing = document.getElementById(SNIPPET_ID);
      if (existing && !(existing instanceof HTMLScriptElement)) {
        reject(
          new ZendeskMessagingError(
            'snippet-load-failed',
            'Zendesk Messaging snippet identifier is already in use'
          )
        );
        return;
      }

      const script = existing ?? document.createElement('script');
      if (!(script instanceof HTMLScriptElement)) {
        reject(
          new ZendeskMessagingError(
            'snippet-load-failed',
            'Zendesk Messaging snippet could not be created'
          )
        );
        return;
      }

      if (existing && !scriptHasSnippetKey(script, snippetKey)) {
        reject(
          new ZendeskMessagingError(
            'snippet-load-failed',
            'A different Zendesk Messaging snippet is already loaded'
          )
        );
        return;
      }

      if (!existing) {
        script.id = SNIPPET_ID;
        script.src = snippetUrl(snippetKey);
        script.async = true;
        script.dataset.iokZendeskMessaging = 'true';
        document.head.appendChild(script);
      }

      if (typeof window.zE === 'function') {
        script.dataset.iokZendeskState = 'ready';
        resolve();
        return;
      }

      let settled = false;
      const finish = (error?: ZendeskMessagingError) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        script.removeEventListener('load', handleLoad);
        script.removeEventListener('error', handleError);

        if (error) {
          reject(error);
          return;
        }
        resolve();
      };
      const handleLoad = () => {
        if (typeof window.zE !== 'function') {
          finish(
            new ZendeskMessagingError(
              'sdk-unavailable',
              'Zendesk Messaging SDK did not initialize after the snippet loaded'
            )
          );
          return;
        }
        script.dataset.iokZendeskState = 'ready';
        finish();
      };
      const handleError = () =>
        finish(
          new ZendeskMessagingError(
            'snippet-load-failed',
            'Zendesk Messaging snippet failed to load'
          )
        );
      const timeoutId = window.setTimeout(
        () =>
          finish(
            new ZendeskMessagingError(
              'readiness-timeout',
              'Zendesk Messaging readiness deadline expired'
            )
          ),
        timeoutMs
      );

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
    }).catch((error: unknown) => {
      this.readyPromise = undefined;
      throw error;
    });

    return this.readyPromise;
  }

  setLocale(locale: string): void {
    this.getApi()('messenger:set', 'locale', locale);
  }

  setCustomization(customization: ZendeskMessagingCustomization): void {
    const customizationKey = JSON.stringify(customization);
    if (this.appliedCustomizationKey === customizationKey) return;

    this.getApi()('messenger:set', 'customization', customization);
    this.appliedCustomizationKey = customizationKey;
  }

  setConversationFields(
    fields: ZendeskConversationField[],
    timeoutMs: number
  ): Promise<void> {
    return this.setContextWithCallback('conversationFields', fields, timeoutMs);
  }

  setConversationTags(tags: string[]): void {
    this.getApi()('messenger:set', 'conversationTags', tags);
  }

  setConversationMetadata(
    metadata: Record<string, ZendeskSupportScalar>,
    timeoutMs: number
  ): Promise<void> {
    return this.setContextWithCallback('conversationMetadata', metadata, timeoutMs);
  }

  setBeforeMessageSent(
    callback: (message: ZendeskOutgoingMessage) => ZendeskOutgoingMessage
  ): () => void {
    const unsubscribe = this.getApi()(
      'messenger:set',
      'beforeMessageSent',
      (message) => callback(message)
    );
    return typeof unsubscribe === 'function' ? unsubscribe : () => undefined;
  }

  subscribeToClose(listener: () => void): () => void {
    const unsubscribe = this.getApi()('messenger:on', 'close', listener);
    return typeof unsubscribe === 'function' ? unsubscribe : () => undefined;
  }

  subscribeToUnreadMessages(
    listener: (count: number) => void
  ): (() => void) | undefined {
    const unsubscribe = this.getApi()('messenger:on', 'unreadMessages', listener);
    return typeof unsubscribe === 'function' ? unsubscribe : undefined;
  }

  open(): void {
    this.getApi()('messenger', 'show');
    this.getApi()('messenger', 'open');
  }

  deactivate(): void {
    const api = this.getApi();
    api('messenger', 'close');
    api('messenger', 'hide');
  }

  renderFloating(): Promise<void> {
    if (this.renderedMode === 'embedded') {
      return Promise.reject(
        new ZendeskMessagingError(
          'mode-conflict',
          'Embedded and floating Zendesk modes cannot share a document'
        )
      );
    }
    if (this.floatingRenderPromise) return this.floatingRenderPromise;

    this.renderedMode = 'floating';
    this.floatingRenderPromise = this.render({ mode: 'floating' }).catch((error) => {
      this.renderedMode = null;
      this.floatingRenderPromise = undefined;
      throw error;
    });
    return this.floatingRenderPromise;
  }

  loginUser(jwtProvider: ZendeskJwtProvider, timeoutMs: number, isCurrent: (initial: boolean) => boolean = () => true): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let failed = false;
      const finish = (error?: unknown) => {
        if (settled) return;
        settled = true;
        failed = Boolean(error);
        window.clearTimeout(timeoutId);
        if (error) {
          reject(
            error instanceof ZendeskMessagingError
              ? error
              : isIdentityConflictError(error)
                ? new ZendeskMessagingError(
                    'identity-conflict',
                    'Zendesk Messaging identity confirmation is required'
                  )
              : new ZendeskMessagingError(
                  'authentication-failed',
                  'Zendesk Messaging authentication failed'
                )
          );
          return;
        }
        resolve();
      };
      let timeoutId = window.setTimeout(
        () => finish(new Error('authentication deadline expired')),
        timeoutMs
      );

      // Resolve the initial identity before starting SDK login. A rejected
      // provider (e.g. our preflight 409) cannot cancel a login already waiting
      // for its callback inside the SDK.
      const startLogin = async () => {
        let initialJwt: string | undefined;
        await jwtProvider((jwt) => { initialJwt = jwt; });
        if (settled || !isCurrent(true) || initialJwt === undefined) return;
        // Resolving identity on our backend must not consume the SDK's login
        // deadline. Both phases remain bounded, and late tokens stay fenced.
        window.clearTimeout(timeoutId);
        timeoutId = window.setTimeout(
          () => finish(new Error('authentication deadline expired')),
          timeoutMs
        );
        // The snippet can replace its bootstrap API while preflight is pending.
        this.getApi()(
          'messenger',
          'loginUser',
          (callback) => {
            if (failed || !isCurrent(initialJwt !== undefined)) {
              initialJwt = undefined;
              return;
            }
            if (initialJwt !== undefined) {
              const jwt = initialJwt;
              initialJwt = undefined;
              callback(jwt);
              return;
            }
            void jwtProvider(callback).catch(finish);
          },
          (error) => finish(error ?? undefined)
        );
      };
      void startLogin().catch(finish);
    });
  }

  renderEmbedded(targets: ZendeskEmbeddedTargets): Promise<void> {
    if (this.renderedMode === 'floating') {
      return Promise.reject(
        new ZendeskMessagingError(
          'mode-conflict',
          'Floating and embedded Zendesk modes cannot share a document'
        )
      );
    }

    let resolved: ResolvedZendeskEmbeddedRender;
    try {
      resolved = this.resolveEmbeddedTargets(targets);
    } catch (error) {
      return Promise.reject(error);
    }

    const { binding, wireTargets } = resolved;
    const inFlight = this.embeddedRenderInFlight;
    if (inFlight) {
      if (embeddedBindingsMatch(inFlight.binding, binding)) {
        return inFlight.promise;
      }
      if (hasConnectedEmbeddedTarget(inFlight.binding)) {
        return this.rejectEmbeddedTargetConflict();
      }
    }

    if (this.embeddedBinding) {
      if (
        embeddedBindingsMatch(this.embeddedBinding, binding) &&
        hasEveryEmbeddedTargetConnected(this.embeddedBinding)
      ) {
        return Promise.resolve();
      }
      if (hasConnectedEmbeddedTarget(this.embeddedBinding)) {
        return this.rejectEmbeddedTargetConflict();
      }
      this.embeddedBinding = undefined;
    }

    this.renderedMode = 'embedded';
    const generation = ++this.embeddedRenderGeneration;
    const promise = this.render(
      {
        mode: 'embedded',
        ...wireTargets
      },
      binding
    )
      .then(() => {
        if (this.embeddedRenderInFlight?.generation === generation) {
          this.embeddedBinding = binding;
        }
      })
      .catch((error) => {
        if (this.embeddedRenderInFlight?.generation === generation) {
          this.renderedMode = null;
          this.embeddedBinding = undefined;
        }
        throw error;
      })
      .finally(() => {
        if (this.embeddedRenderInFlight?.generation === generation) {
          this.embeddedRenderInFlight = undefined;
        }
      });
    this.embeddedRenderInFlight = { generation, binding, promise };
    return promise;
  }

  logoutUser(timeoutMs: number): Promise<void> {
    if (typeof window.zE !== 'function') return Promise.resolve();

    return new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        resolve();
      };
      const timeoutId = window.setTimeout(finish, timeoutMs);

      try {
        // The SDK has no logout callback. This is a bounded grace period, not an acknowledgement.
        window.zE?.('messenger', 'logoutUser');
      } catch {
        finish();
      }
    });
  }

  resetSession(): void {
    // The SDK instance and selected render mode intentionally survive React
    // remounts. Session callbacks are invalidated by the coordinator.
  }

  private setContextWithCallback(
    command: 'conversationFields' | 'conversationMetadata',
    value: ZendeskConversationField[] | Record<string, ZendeskSupportScalar>,
    timeoutMs: number
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (error?: unknown) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        if (error) {
          reject(new Error('sdk-error'));
          return;
        }
        resolve();
      };
      const timeoutId = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error('timeout'));
      }, timeoutMs);
      try {
        if (command === 'conversationFields') {
          this.getApi()('messenger:set', command, value as ZendeskConversationField[], finish);
        } else {
          this.getApi()(
            'messenger:set',
            command,
            value as Record<string, ZendeskSupportScalar>,
            finish
          );
        }
      } catch (error) {
        window.clearTimeout(timeoutId);
        reject(error);
      }
    });
  }

  private resolveEmbeddedTargets(
    targets: ZendeskEmbeddedTargets
  ): ResolvedZendeskEmbeddedRender {
    const binding: ResolvedZendeskEmbeddedTarget[] = [];
    const wireTargets: ZendeskEmbeddedWireTargets = {};

    for (const name of EMBEDDED_TARGET_NAMES) {
      const selector = targets[name];
      if (typeof selector !== 'string' || selector.trim().length === 0) continue;

      let element: Element | null;
      try {
        element = document.querySelector(selector);
      } catch {
        throw new ZendeskMessagingError(
          'render-failed',
          'An embedded Zendesk Messaging selector is invalid'
        );
      }
      if (!element) {
        throw new ZendeskMessagingError(
          'render-failed',
          'An embedded Zendesk Messaging target is unavailable'
        );
      }

      binding.push({ name, selector, element });
      wireTargets[name] = { targetElement: selector };
    }

    if (binding.length === 0) {
      throw new ZendeskMessagingError(
        'render-failed',
        'Embedded Zendesk Messaging requires at least one target'
      );
    }

    return { binding, wireTargets };
  }

  subscribeToConversationOpened(listener: () => void): () => void {
    const unsubscribe = this.getApi()('messenger:on', 'conversationOpened', listener);
    return typeof unsubscribe === 'function' ? unsubscribe : () => undefined;
  }

  private rejectEmbeddedTargetConflict(): Promise<void> {
    return Promise.reject(
      new ZendeskMessagingError(
        'render-failed',
        'Zendesk Messaging is already assigned to other embedded targets'
      )
    );
  }

  private render(
    config: ZendeskRenderConfig,
    embeddedBinding?: ZendeskEmbeddedBinding
  ): Promise<void> {
    const api = this.getApi();
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let timeoutId: number | undefined;

      const finish = (error?: ZendeskSdkError | null) => {
        if (settled) return;
        settled = true;
        if (timeoutId !== undefined) window.clearTimeout(timeoutId);

        if (error) {
          reject(
            new ZendeskMessagingError(
              'render-failed',
              'Zendesk Messaging render failed'
            )
          );
          return;
        }
        resolve();
      };

      if (embeddedBinding) {
        timeoutId = window.setTimeout(
          () => finish({ message: 'embedded render deadline expired' }),
          DEFAULT_READY_TIMEOUT_MS
        );
      }

      try {
        api('messenger', 'render', config, finish);
        if (!embeddedBinding) finish();
      } catch {
        finish({ message: 'render command failed' });
      }
    });
  }
}

type StateListener = (state: ZendeskMessagingState) => void;
type UnreadStateListener = (state: ZendeskUnreadState) => void;

export interface ZendeskMessagingCoordinatorOptions {
  enabled?: boolean;
  snippetKey?: string;
  adapter?: ZendeskMessagingSdkAdapter;
  fetchJwt?: ZendeskMessagingJwtFetcher;
  useExistingIdentity?: ZendeskMessagingIdentityAction;
  finalizeIdentity?: ZendeskMessagingIdentityAction;
  readyTimeoutMs?: number;
  authenticationTimeoutMs?: number;
  logoutTimeoutMs?: number;
  unreadTelemetry?: ChatUnreadTelemetrySink;
}

export interface ZendeskMessagingInitializeOptions {
  session: ZendeskMessagingSession | null;
  language?: string | null;
  onInvalidIokSession?: () => void | Promise<void>;
}

export class ZendeskMessagingCoordinator {
  private state: ZendeskMessagingState = { status: 'disabled', mode: null };
  private unreadState: ZendeskUnreadState = { status: 'unknown', count: null };
  private readonly listeners = new Set<StateListener>();
  private readonly unreadListeners = new Set<UnreadStateListener>();
  private readonly enabled: boolean;
  private readonly snippetKey: string;
  private readonly adapter: ZendeskMessagingSdkAdapter;
  private readonly fetchJwt: ZendeskMessagingJwtFetcher;
  private readonly useExistingIdentityAction: ZendeskMessagingIdentityAction;
  private readonly finalizeIdentityAction: ZendeskMessagingIdentityAction;
  private readonly readyTimeoutMs: number;
  private readonly authenticationTimeoutMs: number;
  private readonly logoutTimeoutMs: number;
  private readonly unreadTelemetry: ChatUnreadTelemetrySink;
  private documentMode: ZendeskMessagingMode | null = null;
  private activeSession: ZendeskMessagingSession | null = null;
  private activeSessionKey?: string;
  private initializedSessionKey?: string;
  private initializedIdentityMode: 'anonymous' | 'authenticated' = 'authenticated';
  private initializationKey?: string;
  private initializationPromise?: Promise<void>;
  private conflictedAuthentication = false;
  private embeddedRenderGeneration = 0;
  private sessionGeneration = 0;
  private unreadPhysicalSubscription = false;
  private unreadSubscriptionGeneration = 0;
  private unreadSubscriptionActive = false;
  private unreadSubscriptionSessionKey?: string;
  private unreadUnsubscribe?: () => void;
  private positiveUnreadPeriod = false;
  private locale = 'pt-BR';
  private appliedLocale?: string;
  private ending = false;
  private onInvalidIokSession?: () => void | Promise<void>;

  constructor(options: ZendeskMessagingCoordinatorOptions = {}) {
    this.enabled = options.enabled ?? isZendeskMessagingEnabled;
    this.snippetKey = (
      options.snippetKey ?? process.env.REACT_APP_ZENDESK_MESSAGING_SNIPPET_KEY ?? ''
    ).trim();
    this.adapter = options.adapter ?? new BrowserZendeskMessagingAdapter();
    this.fetchJwt = options.fetchJwt ?? fetchZendeskMessagingJwt;
    this.useExistingIdentityAction = options.useExistingIdentity ?? useExistingZendeskMessagingIdentity;
    this.finalizeIdentityAction = options.finalizeIdentity ?? finalizeZendeskMessagingIdentity;
    this.readyTimeoutMs = options.readyTimeoutMs ?? DEFAULT_READY_TIMEOUT_MS;
    this.authenticationTimeoutMs =
      options.authenticationTimeoutMs ?? DEFAULT_AUTH_TIMEOUT_MS;
    this.logoutTimeoutMs = options.logoutTimeoutMs ?? DEFAULT_LOGOUT_TIMEOUT_MS;
    this.unreadTelemetry = options.unreadTelemetry ?? emitChatUnreadTelemetry;
  }

  getState(): ZendeskMessagingState {
    return { ...this.state };
  }

  getUnreadState(): ZendeskUnreadState {
    return { ...this.unreadState };
  }

  isEnding(): boolean {
    return this.ending;
  }

  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  subscribeToUnreadState(listener: UnreadStateListener): () => void {
    this.unreadListeners.add(listener);
    listener(this.getUnreadState());
    return () => this.unreadListeners.delete(listener);
  }

  reportChatOpenedFromUnread(source: ChatUnreadSource): void {
    if (
      !this.enabled ||
      this.unreadState.status !== 'known' ||
      this.unreadState.count <= 0
    ) {
      return;
    }

    this.emitUnreadTelemetry({
      name: 'chat_opened_from_unread',
      source,
      count_bucket: getChatUnreadCountBucket(this.unreadState.count)
    });
  }

  subscribeToClose(listener: () => void): () => void {
    if (
      this.documentMode !== 'embedded' ||
      !['authenticated', 'anonymous'].includes(this.state.status) ||
      this.ending
    ) {
      return () => undefined;
    }

    try {
      return this.adapter.subscribeToClose(listener);
    } catch {
      // The platform close button remains available if the event API is unavailable.
      return () => undefined;
    }
  }

  reopenEmbedded(): void {
    if (
      this.documentMode !== 'embedded' ||
      !['authenticated', 'anonymous'].includes(this.state.status) ||
      this.ending
    ) {
      return;
    }

    try {
      this.adapter.open();
    } catch {
      // The retry state remains owned by the existing embedded lifecycle.
    }
  }

  subscribeToConversationOpened(listener: () => void): () => void {
    if (
      this.documentMode !== 'embedded' ||
      !['authenticated', 'anonymous'].includes(this.state.status) ||
      this.ending
    ) {
      return () => undefined;
    }

    try {
      if (!this.adapter.subscribeToConversationOpened) return () => undefined;
      return this.adapter.subscribeToConversationOpened(listener);
    } catch {
      return () => undefined;
    }
  }

  async initialize(options: ZendeskMessagingInitializeOptions): Promise<void> {
    if (this.ending) return;

    this.onInvalidIokSession = options.onInvalidIokSession;
    this.locale = mapIokLanguageToZendeskLocale(options.language);

    if (!this.enabled) {
      this.stopUnreadSubscription({ status: 'unknown', count: null });
      this.setState('disabled', null);
      return;
    }
    if (!this.snippetKey) {
      this.setUnavailable('missing-snippet-key');
      return;
    }
    if (!SNIPPET_KEY_PATTERN.test(this.snippetKey)) {
      this.setUnavailable('invalid-snippet-key');
      return;
    }

    const desiredMode: ZendeskMessagingMode = options.session ? 'embedded' : 'floating';
    if (this.documentMode && this.documentMode !== desiredMode) {
      this.setUnavailable('mode-conflict');
      return;
    }
    this.documentMode = desiredMode;

    const sessionKey = options.session
      ? `authenticated:${String(options.session.userId)}`
      : 'anonymous';
    if (options.session && this.activeSessionKey === sessionKey) {
      this.activeSession = { ...options.session };
    }

    if (this.initializedSessionKey === sessionKey && !this.ending) {
      if (
        desiredMode === 'embedded' &&
        this.state.status === 'unavailable' &&
        this.state.unavailableReason === 'render-failed'
      ) {
        this.setState(this.initializedIdentityMode, desiredMode);
      }
      this.applyLocale();
      return;
    }
    if (this.initializationPromise && this.initializationKey === sessionKey) {
      return this.initializationPromise;
    }

    if (this.activeSessionKey && this.activeSessionKey !== sessionKey) {
      this.stopUnreadSubscription({ status: 'unknown', count: null });
      this.sessionGeneration += 1;
      await this.adapter.logoutUser(this.logoutTimeoutMs);
      this.adapter.resetSession();
    }

    this.ending = false;
    this.activeSession = options.session ? { ...options.session } : null;
    this.activeSessionKey = sessionKey;
    const generation = ++this.sessionGeneration;
    this.initializationKey = sessionKey;
    this.setState('loading', desiredMode);

    const operation = (async () => {
      try {
        await this.resetConflictedAuthentication(generation, sessionKey);
        if (!this.isCurrent(generation, sessionKey)) return;
        await this.adapter.ensureReady(this.snippetKey, this.readyTimeoutMs);
        if (!this.isCurrent(generation, sessionKey)) return;

        this.setState('ready', desiredMode);
        this.applyLocale();

        if (desiredMode === 'floating') {
          await this.adapter.renderFloating();
          if (!this.isCurrent(generation, sessionKey)) return;
          this.initializedSessionKey = sessionKey;
          return;
        }

        this.setState('authenticating', desiredMode);
        await this.authenticateCurrentSession(generation, sessionKey, this.fetchJwt);
        if (!this.isCurrent(generation, sessionKey)) return;

        this.initializedSessionKey = sessionKey;
        this.initializedIdentityMode = 'authenticated';
        this.setState('authenticated', desiredMode);
      } catch (error) {
        if (!this.isCurrent(generation, sessionKey)) return;
        if (isIdentityConflictError(error)) {
          this.setState('identity-conflict', desiredMode, 'identity-conflict');
        } else {
          this.setUnavailable(toUnavailableReason(error));
        }
      }
    })();

    this.initializationPromise = operation.finally(() => {
      if (this.initializationKey === sessionKey) {
        this.initializationKey = undefined;
        this.initializationPromise = undefined;
      }
    });
    return this.initializationPromise;
  }

  updateLocale(language?: string | null): void {
    this.locale = mapIokLanguageToZendeskLocale(language);
    this.applyLocale();
  }

  applyCustomization(customization: ZendeskMessagingCustomization): void {
    if (!['ready', 'authenticating', 'authenticated', 'anonymous'].includes(this.state.status)) {
      return;
    }

    try {
      this.adapter.setCustomization(customization);
    } catch {
      // Visual customization is best effort and must not alter the SDK lifecycle.
    }
  }

  async renderEmbedded(targets: ZendeskEmbeddedTargets): Promise<void> {
    if (
      this.documentMode !== 'embedded' ||
      !['authenticated', 'anonymous'].includes(this.state.status)
    ) {
      throw new ZendeskMessagingError(
        'render-failed',
        'Authenticated embedded Zendesk Messaging is not ready'
      );
    }

    const generation = ++this.embeddedRenderGeneration;
    const sessionGeneration = this.sessionGeneration;
    const sessionKey = this.activeSessionKey;
    try {
      // Rendering can emit the initial count before its completion callback.
      // Authentication is already settled here, including background renders.
      if (sessionKey && this.isCurrent(sessionGeneration, sessionKey)) {
        this.startUnreadSubscription(sessionGeneration, sessionKey);
      }
      await this.adapter.renderEmbedded(targets);
    } catch (error) {
      if (generation === this.embeddedRenderGeneration && !this.ending) {
        this.stopUnreadSubscription({ status: 'unavailable', count: null });
        this.setUnavailable(toUnavailableReason(error));
      }
      throw error;
    }
  }

  markEnding(): void {
    this.ending = true;
    this.stopUnreadSubscription({ status: 'unknown', count: null });
    this.sessionGeneration += 1;
    this.embeddedRenderGeneration += 1;
    this.initializationKey = undefined;
    this.initializationPromise = undefined;
  }

  async logoutUserBestEffort(): Promise<void> {
    this.stopUnreadSubscription({ status: 'unknown', count: null });
    if (!this.enabled || !this.activeSession) return;
    try {
      await this.adapter.logoutUser(this.logoutTimeoutMs);
    } catch {
      // Zendesk must never block the IOK session termination.
    }
  }

  async useExistingIdentity(): Promise<void> {
    const session = this.activeSession;
    const sessionKey = this.activeSessionKey;
    if (!session || !sessionKey || this.state.status !== 'identity-conflict' || this.ending) return;
    const generation = this.sessionGeneration;
    this.setState('authenticating', 'embedded');
    try {
      await this.resetConflictedAuthentication(generation, sessionKey);
      if (!this.isCurrent(generation, sessionKey)) return;
      await this.authenticateCurrentSession(generation, sessionKey, this.useExistingIdentityAction);
      if (!this.isCurrent(generation, sessionKey)) return;
      this.initializedSessionKey = sessionKey;
      this.initializedIdentityMode = 'authenticated';
      this.setState('authenticated', 'embedded');
    } catch (error) {
      if (!this.isCurrent(generation, sessionKey)) return;
      this.setUnavailable(toUnavailableReason(error));
      throw error;
    }
  }

  async continueAnonymously(): Promise<void> {
    const sessionKey = this.activeSessionKey;
    if (!sessionKey || this.state.status !== 'identity-conflict' || this.ending) return;
    const generation = this.sessionGeneration;
    this.setState('authenticating', 'embedded');
    this.stopUnreadSubscription({ status: 'unknown', count: null });
    try {
      await this.adapter.logoutUser(this.logoutTimeoutMs);
    } catch {
      // The explicit anonymous fallback must remain available if logout reports an error.
    }
    if (!this.isCurrent(generation, sessionKey)) return;
    this.adapter.resetSession();
    this.conflictedAuthentication = false;
    this.initializedSessionKey = sessionKey;
    this.initializedIdentityMode = 'anonymous';
    this.setState('anonymous', 'embedded');
  }

  clearIntegrationSession(): void {
    this.conflictedAuthentication = false;
    this.stopUnreadSubscription({ status: 'unknown', count: null });
    this.adapter.resetSession();
    this.embeddedRenderGeneration += 1;
    this.activeSession = null;
    this.activeSessionKey = undefined;
    this.initializedSessionKey = undefined;
    this.initializedIdentityMode = 'authenticated';
    this.appliedLocale = undefined;
    this.onInvalidIokSession = undefined;
    this.setState(this.enabled ? 'ready' : 'disabled', this.documentMode);
  }

  private createJwtProvider(
    generation: number,
    sessionKey: string,
    fetchToken: ZendeskMessagingIdentityAction = this.fetchJwt,
    onToken?: (response: ZendeskMessagingJwtResponse) => void
  ): ZendeskJwtProvider {
    return async (callback) => {
      const session = this.activeSession;
      if (!session || !this.isCurrent(generation, sessionKey)) return;
      const accessToken = session.accessToken;

      try {
        const response = await fetchToken(accessToken);
        if (
          !this.isCurrent(generation, sessionKey) ||
          this.activeSession?.accessToken !== accessToken
        ) {
          return;
        }
        onToken?.(response);
        callback(response.jwt);
      } catch (error) {
        if (!this.isCurrent(generation, sessionKey)) return;

        const status = getHttpStatus(error);
        if (status === 409 || isIdentityConflictError(error)) {
          if (this.state.status === 'authenticated') {
            this.initializedSessionKey = undefined;
            this.conflictedAuthentication = true;
            this.sessionGeneration += 1;
            this.embeddedRenderGeneration += 1;
            this.stopUnreadSubscription({ status: 'unknown', count: null });
            try { this.adapter.deactivate(); } catch { /* The conflict screen still hides the chat. */ }
          }
          this.setState('identity-conflict', 'embedded', 'identity-conflict');
        } else if (status === 503) {
          this.setUnavailable('service-unavailable');
        } else {
          this.setUnavailable('authentication-failed');
          if (status === 401 || status === 403) {
            void this.onInvalidIokSession?.();
          }
        }
        throw new ZendeskMessagingError(
          status === 409 || isIdentityConflictError(error)
            ? 'identity-conflict'
            : status === 503 ? 'service-unavailable' : 'authentication-failed',
          'Zendesk Messaging could not obtain authentication'
        );
      }
    };
  }

  private async resetConflictedAuthentication(generation: number, sessionKey: string): Promise<void> {
    if (!this.conflictedAuthentication) return;
    try {
      await this.adapter.logoutUser(this.logoutTimeoutMs);
    } catch {
      // The SDK logout is best-effort; stale callbacks are already invalidated.
    }
    if (!this.isCurrent(generation, sessionKey)) return;
    this.adapter.resetSession();
    this.conflictedAuthentication = false;
  }

  private async authenticateCurrentSession(
    generation: number,
    sessionKey: string,
    fetchInitialToken: ZendeskMessagingIdentityAction
  ): Promise<void> {
    let requiresFinalization = false;
    const accessToken = this.activeSession?.accessToken;
    const canDeliverToken = (initial: boolean) => this.isCurrent(generation, sessionKey) &&
      (!initial || this.activeSession?.accessToken === accessToken);
    await this.adapter.loginUser(
      this.createJwtProvider(
        generation,
        sessionKey,
        fetchInitialToken,
        (response) => { requiresFinalization = response.requiresIdentityFinalization === true; }
      ),
      this.authenticationTimeoutMs,
      canDeliverToken
    );
    if (!requiresFinalization || !this.isCurrent(generation, sessionKey)) return;

    const session = this.activeSession;
    if (!session) return;
    try {
      await this.finalizeIdentityAction(session.accessToken);
      if (!this.isCurrent(generation, sessionKey)) return;
    } catch (error) {
      if (!this.isCurrent(generation, sessionKey)) return;
      try {
        await this.adapter.logoutUser(this.logoutTimeoutMs);
      } catch {
        // Reset still invalidates local SDK state before surfacing finalization failure.
      }
      if (!this.isCurrent(generation, sessionKey)) return;
      this.adapter.resetSession();
      throw error;
    }
    await this.adapter.logoutUser(this.logoutTimeoutMs);
    if (!this.isCurrent(generation, sessionKey)) return;
    this.adapter.resetSession();
    await this.adapter.loginUser(
      this.createJwtProvider(generation, sessionKey),
      this.authenticationTimeoutMs,
      canDeliverToken
    );
  }

  deactivateEmbedded(): void {
    if (
      this.documentMode !== 'embedded' ||
      !['authenticated', 'anonymous'].includes(this.state.status) ||
      this.ending
    ) {
      return;
    }

    try {
      this.adapter.deactivate();
    } catch {
      // Navigation must remain available if the SDK cannot be closed.
    }
  }

  private startUnreadSubscription(
    sessionGeneration: number,
    sessionKey: string
  ): void {
    if (
      (
        this.unreadSubscriptionActive &&
        this.unreadSubscriptionSessionKey === sessionKey
      ) ||
      !this.isCurrent(sessionGeneration, sessionKey)
    ) {
      return;
    }

    this.unreadSubscriptionActive = true;
    this.unreadSubscriptionSessionKey = sessionKey;
    this.setUnreadState({ status: 'unknown', count: null });

    if (this.unreadPhysicalSubscription) return;

    const subscriptionGeneration = ++this.unreadSubscriptionGeneration;
    try {
      const unsubscribe = this.adapter.subscribeToUnreadMessages(
        (count) => {
          if (subscriptionGeneration !== this.unreadSubscriptionGeneration) return;
          this.handleUnreadMessage(count, sessionGeneration, sessionKey);
        }
      );
      this.unreadPhysicalSubscription = true;
      this.unreadUnsubscribe = unsubscribe;

      if (!this.isCurrent(sessionGeneration, sessionKey)) {
        this.stopUnreadSubscription({ status: 'unknown', count: null });
        return;
      }
    } catch {
      this.unreadPhysicalSubscription = false;
      this.unreadSubscriptionActive = false;
      this.unreadSubscriptionSessionKey = undefined;
      this.unreadUnsubscribe = undefined;
      if (this.isCurrent(sessionGeneration, sessionKey)) {
        this.setUnreadState({ status: 'unavailable', count: null });
      }
    }
  }

  private stopUnreadSubscription(nextState: ZendeskUnreadState): void {
    this.unreadSubscriptionGeneration += 1;
    this.unreadSubscriptionActive = false;
    this.unreadSubscriptionSessionKey = undefined;
    const unsubscribe = this.unreadUnsubscribe;
    this.unreadUnsubscribe = undefined;
    this.unreadPhysicalSubscription = false;

    if (unsubscribe) {
      try {
        unsubscribe();
      } catch {
        // The captured generation keeps this callback inert if cleanup fails.
      }
    }

    this.setUnreadState(nextState);
  }

  private handleUnreadMessage(
    count: number,
    sessionGeneration: number,
    sessionKey: string
  ): void {
    if (
      !this.unreadSubscriptionActive ||
      this.unreadSubscriptionSessionKey !== sessionKey ||
      !this.isCurrent(sessionGeneration, sessionKey) ||
      !Number.isInteger(count) ||
      count < 0
    ) {
      return;
    }

    this.setUnreadState({ status: 'known', count });
  }

  private setUnreadState(next: ZendeskUnreadState): void {
    if (
      this.unreadState.status === next.status &&
      this.unreadState.count === next.count
    ) {
      return;
    }

    this.unreadState = next;
    const isPositive = next.status === 'known' && next.count > 0;
    if (isPositive && !this.positiveUnreadPeriod) {
      this.emitUnreadTelemetry({
        name: 'chat_unread_badge_shown',
        count_bucket: getChatUnreadCountBucket(next.count)
      });
    }
    this.positiveUnreadPeriod = isPositive;
    this.unreadListeners.forEach((listener) => listener(this.getUnreadState()));
  }

  private emitUnreadTelemetry(
    event: Parameters<ChatUnreadTelemetrySink>[0]
  ): void {
    try {
      this.unreadTelemetry(event);
    } catch {
      // Telemetry must never affect the Zendesk or IOK lifecycle.
    }
  }

  private applyLocale(): void {
    if (
      this.appliedLocale === this.locale ||
      !['ready', 'authenticating', 'authenticated', 'anonymous'].includes(this.state.status)
    ) {
      return;
    }

    try {
      this.adapter.setLocale(this.locale);
      this.appliedLocale = this.locale;
    } catch {
      this.setUnavailable('sdk-unavailable');
    }
  }

  private isCurrent(generation: number, sessionKey: string): boolean {
    return (
      !this.ending &&
      generation === this.sessionGeneration &&
      sessionKey === this.activeSessionKey
    );
  }

  private setUnavailable(reason: ZendeskUnavailableReason): void {
    this.setState('unavailable', this.documentMode, reason);
  }

  private setState(
    status: ZendeskMessagingStatus,
    mode: ZendeskMessagingMode | null,
    unavailableReason?: ZendeskUnavailableReason
  ): void {
    if (!isZendeskMessagingTransitionValid(this.state.status, status)) {
      throw new Error(`Invalid Zendesk Messaging transition: ${this.state.status} -> ${status}`);
    }

    const next: ZendeskMessagingState = {
      status,
      mode,
      ...(unavailableReason ? { unavailableReason } : {})
    };
    if (
      this.state.status === next.status &&
      this.state.mode === next.mode &&
      this.state.unavailableReason === next.unavailableReason
    ) {
      return;
    }

    this.state = next;
    this.listeners.forEach((listener) => listener(this.getState()));
  }
}

export const zendeskMessagingAdapter = new BrowserZendeskMessagingAdapter();
export const zendeskMessagingCoordinator = new ZendeskMessagingCoordinator({
  adapter: zendeskMessagingAdapter
});
